import { BASE_URL } from '@helpers/constants'
import setAuthorization from '@helpers/setAuthorization'
import {
	ProfileAchievementResponse,
	ProfileCommentResponse,
	ProfileFollowersResponse,
	ProfileInformations,
	ProfileLocation,
	ProfilePodium,
	ProfilePrivacyPreferences,
} from '@models/profile.model'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface ResearchRequest {
	researchOriginSkip: boolean
	researchOriginOption?: string | null
	researchOriginOptionOther?: string
}

export interface ResearchResponse {
	clientId?: number
}

export const profileAPI = createApi({
	reducerPath: 'profileAPI',
	tagTypes: ['Profile', 'Followers', 'Following'],
	keepUnusedDataFor: 10,
	baseQuery: fetchBaseQuery({
		baseUrl: BASE_URL,
		prepareHeaders: (headers, { endpoint, getState }) =>
			setAuthorization(headers, { getState, endpoint }),
	}),
	endpoints: (builder) => ({
		listAchievements: builder.query<ProfileAchievementResponse, string | void>({
			query: (clientId) => ({
				url: `/client/achievements/${clientId ?? ''}`,
				method: 'GET',
			}),
			providesTags: ['Profile'],
		}),

		likeAchievement: builder.mutation<void, { clientAchievementId: number }>({
			query: ({ clientAchievementId }) => ({
				url: `/client/achievements/${clientAchievementId}/like`,
				method: 'POST',
			}),
			invalidatesTags: ['Profile'],
		}),

		unlikeAchievement: builder.mutation<void, { clientAchievementId: number }>({
			query: ({ clientAchievementId }) => ({
				url: `/client/achievements/${clientAchievementId}/like`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Profile'],
		}),

		listComments: builder.query<ProfileCommentResponse, string | void>({
			query: (clientId) => ({
				url: `/client/comments/${clientId ?? ''}`,
				method: 'GET',
			}),
			providesTags: ['Profile'],
		}),

		listFollowers: builder.query<ProfileFollowersResponse, string | void>({
			query: (clientId) => ({
				url: `/client/followers/${clientId ?? ''}`,
				method: 'GET',
			}),
			providesTags: ['Followers'],
		}),

		listFollowing: builder.query<ProfileFollowersResponse, string | void>({
			query: (clientId) => ({
				url: `/client/following/${clientId ?? ''}`,
				method: 'GET',
			}),
			providesTags: ['Following'],
		}),

		followUser: builder.mutation<void, { followedId: string }>({
			query: (body) => ({
				url: '/client/following',
				method: 'POST',
				body,
				invalidatesTags: ['Following', 'Followers'],
			}),
		}),

		unfollowUser: builder.mutation<void, { followedId: string }>({
			query: ({ followedId }) => ({
				url: `/client/following/${followedId}`,
				method: 'DELETE',
				invalidatesTags: ['Following', 'Followers'],
			}),
		}),

		acceptFollow: builder.mutation<void, { id: number | string }>({
			query: ({ id }) => ({
				url: `/client/followers/${id}`,
				method: 'PUT',
				body: { status: 'accepted' },
				invalidatesTags: ['Followers', 'Following'],
			}),
		}),

		rejectFollow: builder.mutation<void, { id: number | string }>({
			query: ({ id }) => ({
				url: `/client/followers/${id}`,
				method: 'PUT',
				body: { status: 'rejected' },
				invalidatesTags: ['Followers', 'Following'],
			}),
		}),

		getProfileSettings: builder.query<ProfilePrivacyPreferences, string | void>(
			{
				query: (clientId) => ({
					url: clientId
						? `/client/profile-settings/${clientId}`
						: '/client/profile-settings',
					method: 'GET',
				}),
				providesTags: ['Profile'],
			}
		),

		updateProfileSettings: builder.mutation<
			ProfilePrivacyPreferences,
			Partial<ProfilePrivacyPreferences>
		>({
			query: (body) => ({
				url: '/client/profile-settings',
				method: 'POST',
				body,
			}),
		}),

		getProfileInformation: builder.query<ProfileInformations, string>({
			query: (clientId) => ({
				url: `/client/profile/${clientId}/information`,
				method: 'GET',
			}),
			providesTags: ['Profile'],
		}),

		getProfilePodium: builder.query<ProfilePodium, string>({
			query: (clientId) => ({
				url: `/client/profile/${clientId}/podium`,
				method: 'GET',
			}),
			providesTags: ['Profile'],
		}),

		getProfileLocation: builder.query<ProfileLocation, string>({
			query: (clientId) => ({
				url: `/client/profile/${clientId}/location`,
				method: 'GET',
			}),
			providesTags: ['Profile'],
		}),
	}),
})
